import { Alert, ErrorMessage, LoadingState } from '../../../shared/styles/components/feedback.styles';
import { Caption, HintText, InfoLabel, InfoRow, InfoValue, Label, SectionDescription, SectionLabel, TruncatedText } from '../../../shared/styles/components/primitives.styles';
import {
  DetailsCard,
  InfoCard,
  InfoGrid,
  InputSection,
  ScopeAlertContent,
  ScopeAlertText,
  ScopeCard,
  ScopeLabel,
  ScopeSection,
  SpanTwoColumns,
} from './MaterialGradeModal.styles';
import { ExpandToggle, FieldRow, Input, RadioDesc, RadioGroup, RadioOption, RadioTitle } from '../../../shared/styles/components/forms.styles';
import Loading, { LoadingBall } from '@ingka/loading';

import Button from '@ingka/button';
import { IconButton } from '../../../shared/components/IconButton';
import { ItemListSection } from './ItemListSection';
import { MAX_GRADE_ID_LENGTH } from '../types/material-grade.constants';
import { MappingResultCard } from './MappingResultCard';
import { Modal } from '../../../shared/styles/components/modal.styles';
import React from 'react';
import ReactDOM from 'react-dom';
import SSRIcon from '@ingka/ssr-icon';
import { ScopeCheckbox } from './ScopeCheckbox';
import crossIcon from '@ingka/ssr-icon/paths/cross';
import { pluralize } from '../../../shared/utils/formatters.utils';
import styled from '@emotion/styled';
import { tokens } from '../../../shared/styles/core/tokens';
import { useMaterialGradeModal } from '../hooks/useMaterialGradeModal';
import warningTriangleIcon from '@ingka/ssr-icon/paths/warning-triangle';

const InputLabel = styled(Label)`
   display: block;
   margin-bottom: ${tokens.space.sm};
`;

interface MaterialGradeModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: {
    materialGradeId: string;
    isFamilyNameSpecific: boolean;
    isMaterialPartSpecific: boolean;
    isItemNoSpecific: boolean;
    excludedItemNos: string[];
  }) => void;
  itemNo: string;
  familyName: string;
  materialPart: string;
  materialGrade: string;
  currentMaterialGradeId?: string;
}

export const MaterialGradeModal: React.FC<MaterialGradeModalProps> = (props) => {
  const {
    visible,
    onClose,
    onConfirm,
    itemNo,
    familyName,
    materialPart,
    materialGrade,
    currentMaterialGradeId,
  } = props;

  const {
    loading,
    mappingState,
    error,
    newMaterialGradeId,
    inputError,
    scope,
    showDetails,
    excludedItems,
    showHighImpactWarning,
    inputRef,
    totalAffectedCount,
    affectedCount,
    totalExcludedCount,
    isHighImpact,
    buttonConfig,
    isScopeSelected,
    filteredAffectedItems,
    systemExcludedItems,
    setShowHighImpactWarning,
    setShowDetails,
    fetchMappingData,
    handleMaterialGradeIdChange,
    getScopeDescription,
    handleScopeChange,
    handleKeyDownInput,
  } = useMaterialGradeModal(props);

  const handleConfirm = () => {
    if (isHighImpact && !showHighImpactWarning) {
      setShowHighImpactWarning(true);
      return;
    }

    let isFamilyNameSpecific = false;
    let isMaterialPartSpecific = false;
    let isItemNoSpecific = false;

    switch (scope.type) {
      case 'item':
        isMaterialPartSpecific = true;
        isFamilyNameSpecific = true;
        isItemNoSpecific = true;
        break;
      case 'filters':
        isMaterialPartSpecific = scope.filters.part;
        isFamilyNameSpecific = scope.filters.family;
        isItemNoSpecific = false;
        break;
      case 'all':
        isMaterialPartSpecific = false;
        isFamilyNameSpecific = false;
        isItemNoSpecific = false;
        break;
    }

    onConfirm({
      materialGradeId: newMaterialGradeId,
      isFamilyNameSpecific,
      isMaterialPartSpecific,
      isItemNoSpecific,
      excludedItemNos: excludedItems,
    });
  };

  if (!visible) return null;

  return ReactDOM.createPortal(
    <Modal.Overlay>
      <Modal.Container
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="material-grade-modal-title"
      >
        <Modal.Header>
          <Modal.Title id="material-grade-modal-title">Validate Material Grade Change</Modal.Title>
          <IconButton onClick={onClose} icon={crossIcon} title="Close modal" />
        </Modal.Header>

        <Modal.Content>
          <InfoCard>
            <InfoGrid>
              <InfoRow>
                <InfoLabel>Current Item</InfoLabel>
                <InfoValue>{itemNo}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Part</InfoLabel>
                <InfoValue>{materialPart || '(not specified)'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Current Grade ID</InfoLabel>
                <InfoValue>{currentMaterialGradeId || 'None'}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Family</InfoLabel>
                <InfoValue>{familyName || '(not specified)'}</InfoValue>
              </InfoRow>
              <SpanTwoColumns>
                <InfoRow>
                  <InfoLabel>Material Grade</InfoLabel>
                  <InfoValue>{materialGrade || '(not specified)'}</InfoValue>
                </InfoRow>
              </SpanTwoColumns>
            </InfoGrid>
          </InfoCard>

          <ScopeSection>
            <SectionLabel>Mapping Scope</SectionLabel>
            <SectionDescription variant="body-s" color="tertiary">
              Choose how to apply this change. You can combine filters, or select a single-item or global scope.
            </SectionDescription>

            <ScopeCard>
              <SectionLabel>SCOPE</SectionLabel>
              <RadioGroup>
                <RadioOption>
                  <input
                    type="radio"
                    id="item-scope"
                    name="scope-type"
                    checked={scope.type === 'item'}
                    onChange={() => handleScopeChange('item', true)}
                  />
                  <label htmlFor="item-scope">
                    <RadioTitle>This item only</RadioTitle>
                    <RadioDesc>Apply only to item "{itemNo}"</RadioDesc>
                  </label>
                </RadioOption>
                <RadioOption>
                  <input
                    type="radio"
                    id="all-scope"
                    name="scope-type"
                    checked={scope.type === 'all'}
                    onChange={() => handleScopeChange('all', true)}
                  />
                  <label htmlFor="all-scope">
                    <RadioTitle>All items</RadioTitle>
                    <RadioDesc>
                      Apply to all items with "<TruncatedText>{materialGrade}</TruncatedText>"
                    </RadioDesc>
                  </label>
                </RadioOption>
              </RadioGroup>
            </ScopeCard>

            <ScopeCard>
              <SectionLabel>FILTERS</SectionLabel>
              <SectionDescription variant="body-s" color="tertiary">
                Or define scope using filters
              </SectionDescription>
              <RadioGroup>
                <ScopeCheckbox
                  id="part-scope"
                  checked={scope.filters.part}
                  onChange={(checked) => handleScopeChange('part', checked)}
                  title="For this part"
                  description={`Restrict to part "${materialPart}"`}
                />
                <ScopeCheckbox
                  id="family-scope"
                  checked={scope.filters.family}
                  onChange={(checked) => handleScopeChange('family', checked)}
                  disabled={!scope.filters.part}
                  title="For this family"
                  description={
                    !scope.filters.part ? 'Only available with part filter' : `Restrict to family "${familyName}"`
                  }
                />
              </RadioGroup>
            </ScopeCard>

            <Alert variant="info">
              <ScopeAlertContent>
                <ScopeLabel>Current scope:</ScopeLabel>
                <ScopeAlertText>{getScopeDescription()}</ScopeAlertText>
              </ScopeAlertContent>
            </Alert>
          </ScopeSection>

          <InputSection>
            <InputLabel htmlFor="new-grade-id">
              New Material Grade ID <Caption>(4-digit number or 0 for unassigned)</Caption>
            </InputLabel>
            <FieldRow>
              <Input
                ref={inputRef}
                id="new-grade-id"
                type="text"
                value={newMaterialGradeId}
                onChange={handleMaterialGradeIdChange}
                onKeyDown={handleKeyDownInput}
                placeholder="Enter new Material Grade ID"
                hasError={!!inputError}
                maxLength={MAX_GRADE_ID_LENGTH}
                aria-invalid={!!inputError}
                aria-describedby={inputError ? 'grade-id-error' : undefined}
              />
              <Button
                text={loading ? 'Checking...' : scope.type === 'item' ? 'Confirm' : 'Check Impact'}
                type="secondary"
                small={true}
                onClick={fetchMappingData}
                disabled={loading || !newMaterialGradeId || !isScopeSelected}
              />
            </FieldRow>
            {inputError && (
              <ErrorMessage id="grade-id-error" role="alert">
                {inputError}
              </ErrorMessage>
            )}
            {!inputError && newMaterialGradeId === '0' && (
              <HintText color="tertiary" variant="body-s">
                This will mark the material as unassigned
              </HintText>
            )}
          </InputSection>

          {loading && (
            <LoadingState>
              <Loading text="Checking material mapping..." labelPosition="center">
                <LoadingBall size="medium" color="primary" />
              </Loading>
            </LoadingState>
          )}

          {error && (
            <Alert variant="error" role="alert">
              {error}
            </Alert>
          )}

          {!loading && !error && mappingState.data && (
            <>
              {mappingState.data.isAp && (
                <Alert variant="warning">
                  <SSRIcon paths={warningTriangleIcon} />
                  <span>
                    <strong>Asia-Pacific (AP) scope:</strong> only AP-articles will be affected.
                  </span>
                </Alert>
              )}

              <MappingResultCard
                mapping={mappingState.data}
                newGradeId={newMaterialGradeId}
                currentGradeId={currentMaterialGradeId}
                affectedCount={affectedCount}
                excludedCount={totalExcludedCount}
                isHighImpact={isHighImpact}
              />

              {affectedCount === 0 ? (
                <Alert variant="info">
                  <span>No items will be affected by this change. All items matching this scope have been excluded.</span>
                </Alert>
              ) : totalAffectedCount && totalAffectedCount > 1 ? (
                <>
                  <ExpandToggle onClick={() => setShowDetails(!showDetails)} aria-expanded={showDetails}>
                    <span>{showDetails ? '▼' : '▶'}</span>
                    <span>{showDetails ? 'Hide List' : 'Show affected items'}</span>
                  </ExpandToggle>

                  {showDetails && (
                    <DetailsCard>
                      <ItemListSection
                        title="Affected Items"
                        count={affectedCount || 0}
                        items={filteredAffectedItems}
                        type="affected"
                      />

                      {systemExcludedItems.length > 0 && (
                        <ItemListSection
                          title="System Excluded"
                          count={systemExcludedItems.length}
                          items={systemExcludedItems}
                          type="excluded"
                        />
                      )}
                    </DetailsCard>
                  )}
                </>
              ) : null}

              {isHighImpact && !showHighImpactWarning && (
                <Alert variant="error">
                  <SSRIcon paths={warningTriangleIcon} />
                  <span>
                    <strong>High Impact Change:</strong> This will affect {pluralize(affectedCount!)}.
                    {scope.type === 'all' && ' This will impact ALL items with this material grade.'}
                  </span>
                </Alert>
              )}

              {affectedCount && !isHighImpact && affectedCount > 1 && (
                <Alert variant="warning">
                  <SSRIcon paths={warningTriangleIcon} />
                  <span>
                    This change will affect <strong>{pluralize(affectedCount)}</strong>. Please review carefully before
                    applying.
                  </span>
                </Alert>
              )}

              {showHighImpactWarning && (
                <Alert variant="error">
                  <SSRIcon paths={warningTriangleIcon} />
                  <span>
                    <strong>Confirm High Impact Change:</strong> You are about to modify {pluralize(affectedCount!)}.
                    This action cannot be undone. Please confirm you want to proceed.
                  </span>
                </Alert>
              )}
            </>
          )}
        </Modal.Content>

        <Modal.Footer>
          <Button text="Cancel" type="secondary" onClick={onClose} />
          {buttonConfig.showGoBack && (
            <Button text="Go Back" type="secondary" onClick={() => setShowHighImpactWarning(false)} />
          )}
          <Button
            text={buttonConfig.primaryText}
            type={buttonConfig.primaryType}
            onClick={handleConfirm}
            disabled={buttonConfig.primaryDisabled}
          />
        </Modal.Footer>
      </Modal.Container>
    </Modal.Overlay>,
    document.body
  );
};